import {
  BROWSER_LARGE_MAP_SIZES,
  generateLargeMapFixture,
  generateProductionLargeMapFixture,
} from '../testFixtures/largeMapFixtures';

export const REVIEW_MAP_FIXTURE = Object.freeze({
  mindmapId: 'local-review-map',
  nodes: [
    {
      id: 'review-root',
      text: 'Project launch',
      position: { x: 520, y: 320 },
      layoutSize: { width: 260, height: 110 },
      layout: { side: 'right', order: 0, pinned: true },
      color: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
      isRoot: true,
    },
    {
      id: 'review-research',
      text: 'Research customers',
      position: { x: 120, y: 220 },
      layoutSize: { width: 260, height: 110 },
      layout: { side: 'left', order: 0, pinned: false },
      color: 'linear-gradient(135deg, #0891b2 0%, #0f766e 100%)',
    },
    {
      id: 'review-release',
      text: 'Prepare release',
      position: { x: 920, y: 420 },
      layoutSize: { width: 260, height: 110 },
      layout: { side: 'right', order: 0, pinned: false },
      color: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)',
    },
  ],
  relationships: [
    { parentId: 'review-root', childId: 'review-research' },
    { parentId: 'review-root', childId: 'review-release' },
  ],
  root: 'review-root',
  selectedId: 'review-root',
});

const OVERLAPPING_ROUTING_GEOMETRY = [
  [126.72, 136.82, 260, 110], [-541.19, -87.61, 260, 110],
  [-599.77, 71.92, 260, 110], [-598.83, 251.36, 260, 110],
  [-530, -80, 260, 110], [-142.97, 211.27, 260, 110],
  [315.16, 394.26, 260, 110], [377.45, 482.84, 260, 110],
  [326.99, 644.46, 260, 110], [575.27, 581.94, 260, 110],
  [177.08, 533.89, 260, 110], [-462.70, -348.98, 96, 44],
  [-326.31, -413.15, 96, 44], [-49.38, 146.32, 96, 44],
  [19.78, -7.91, 96, 44], [-500.98, -249.49, 96, 44],
  [-129.79, 72.92, 96, 44], [542, 303, 260, 110],
  [712, 265, 260, 110], [712, 342, 260, 110],
  [300, 300, 260, 110], [-49.38, -134.58, 96, 44],
  [89.29, 330.53, 96, 44], [712, 419, 260, 110],
  [882, 523, 260, 110],
];

const OVERLAPPING_ROUTING_EDGES = [
  [14, 3], [14, 2], [14, 4], [14, 11], [14, 15], [14, 1], [14, 12],
  [20, 14], [0, 21], [0, 5], [0, 16], [20, 17], [20, 0], [7, 9],
  [17, 23], [23, 24], [0, 22], [6, 10], [0, 13], [17, 19], [17, 18],
  [7, 8], [6, 7], [20, 6],
];

export const OVERLAPPING_ROUTING_REVIEW_MAP_FIXTURE = Object.freeze({
  mindmapId: 'local-overlapping-routing-review-map',
  nodes: OVERLAPPING_ROUTING_GEOMETRY.map(([x, y, width, height], index) => ({
    id: `routing-node-${index}`,
    text: `Routing node ${index}`,
    position: { x, y },
    layoutSize: { width, height },
    layout: { side: x < 300 ? 'left' : 'right', order: index, pinned: index === 20 },
    color: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
    isRoot: index === 20,
  })),
  relationships: OVERLAPPING_ROUTING_EDGES.map(([parentIndex, childIndex]) => ({
    parentId: `routing-node-${parentIndex}`,
    childId: `routing-node-${childIndex}`,
  })),
  root: 'routing-node-20',
  selectedId: 'routing-node-20',
});

const SIBLING_ROUTING_REVIEW_MAP_FIXTURE = Object.freeze({
  mindmapId: 'local-sibling-routing-review-map',
  nodes: [
    { id: 'fan-parent', text: 'Projects', position: { x: 180, y: 330 }, isRoot: true },
    { id: 'fan-near-up', text: 'Near upper', position: { x: 420, y: 250 } },
    { id: 'fan-far-up', text: 'Far upper', position: { x: 640, y: 130 } },
    { id: 'fan-near-down', text: 'Near lower', position: { x: 420, y: 410 } },
    { id: 'fan-far-down', text: 'Far lower', position: { x: 640, y: 530 } },
  ].map((node, index) => ({
    ...node,
    layoutSize: { width: 100, height: 34 },
    layout: { side: 'right', order: index, pinned: true },
    color: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
  })),
  relationships: ['near-up', 'far-up', 'near-down', 'far-down'].map(child => ({
    parentId: 'fan-parent', childId: `fan-${child}`,
  })),
  root: 'fan-parent',
  selectedId: 'fan-parent',
});

const LARGE_MAP_FIXTURE_PREFIX = 'large-';
const PRODUCTION_LARGE_MAP_FIXTURE = 'large-production-1000';
const largeMapFixtureCache = new Map();
let productionLargeMapFixture = null;

function getLargeMapReviewFixture(fixtureName) {
  if (!fixtureName?.startsWith(LARGE_MAP_FIXTURE_PREFIX)) return null;
  const rawNodeCount = fixtureName.slice(LARGE_MAP_FIXTURE_PREFIX.length);
  if (!/^\d+$/.test(rawNodeCount)) return null;
  const nodeCount = Number.parseInt(rawNodeCount, 10);
  if (!BROWSER_LARGE_MAP_SIZES.includes(nodeCount)) return null;
  if (!largeMapFixtureCache.has(nodeCount)) {
    largeMapFixtureCache.set(nodeCount, Object.freeze({
      ...generateLargeMapFixture(nodeCount),
      mindmapId: `local-large-${nodeCount}-review-map`,
    }));
  }
  return largeMapFixtureCache.get(nodeCount);
}

export function getReviewMapFixture(search = '') {
  const fixture = new URLSearchParams(search).get('fixture');
  if (fixture === 'medium-routing') return {
    ...generateLargeMapFixture(100),
    mindmapId: 'local-medium-routing-review-map',
  };
  if (fixture === 'image-keyboard') return {
    ...REVIEW_MAP_FIXTURE,
    mindmapId: 'local-image-keyboard-review-map',
    nodes: REVIEW_MAP_FIXTURE.nodes.map((node) => node.id === 'review-release'
      ? { ...node, attachment: { assetId: 'fixture-image', kind: 'image', filename: 'diagram.png' } }
      : node),
  };
  if (fixture === 'sibling-routing') return SIBLING_ROUTING_REVIEW_MAP_FIXTURE;
  if (fixture === 'overlapping-routing') return OVERLAPPING_ROUTING_REVIEW_MAP_FIXTURE;
  if (fixture === PRODUCTION_LARGE_MAP_FIXTURE) {
    if (!productionLargeMapFixture) {
      productionLargeMapFixture = Object.freeze({
        ...generateProductionLargeMapFixture(),
        mindmapId: 'local-large-production-1000-review-map',
      });
    }
    return productionLargeMapFixture;
  }
  return getLargeMapReviewFixture(fixture) || REVIEW_MAP_FIXTURE;
}
